import { useCallback, useEffect, useState } from "react";

import {
  type AegOccurrenceComment,
  type AegOccurrenceId,
  createAegComment,
  deleteAegComment,
  fetchAegComments,
  updateAegComment,
} from "../api/aegOccurrences";

const parseUtcIsoStringToDate = (value: string): Date | undefined => {
  let normalized = value;
  const hasTimezone = /Z$/.test(normalized) || /([+-]\d{2}:\d{2}|[+-]\d{2}\d{2})$/.test(normalized);
  if (!hasTimezone) normalized = `${normalized}Z`;
  normalized = normalized.replace(/(\.\d{3})\d+(?=(Z|[+-]\d{2}:?\d{2})$)/, "$1");
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

const normalizeComment = (raw: AegOccurrenceComment): AegOccurrenceComment => ({
  ...raw,
  createdAt:
    raw.createdAt instanceof Date
      ? raw.createdAt
      : typeof raw.createdAt === "string"
        ? parseUtcIsoStringToDate(raw.createdAt)
        : undefined,
});

// Mais recente primeiro, como no chat do support.
const sortByCreatedAt = (list: AegOccurrenceComment[]) =>
  [...list].sort((a, b) => (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0));

export const useAegOccurrenceChat = (aegOccurrenceId: AegOccurrenceId) => {
  const [comments, setComments] = useState<AegOccurrenceComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [newCommentText, setNewCommentText] = useState("");
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const [submittingNew, setSubmittingNew] = useState(false);
  const [savingEditUuid, setSavingEditUuid] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<AegOccurrenceComment | null>(null);

  const loadComments = useCallback(async () => {
    if (!aegOccurrenceId) {
      setComments([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const data = await fetchAegComments(aegOccurrenceId);
      setComments(sortByCreatedAt((data ?? []).map(normalizeComment)));
    } catch {
      setComments([]);
    } finally {
      setLoading(false);
    }
  }, [aegOccurrenceId]);

  useEffect(() => {
    void loadComments();
  }, [loadComments]);

  useEffect(() => {
    setEditingCommentId(null);
    setEditDraft("");
  }, [aegOccurrenceId]);

  const submitNewComment = async () => {
    const trimmed = newCommentText.trim();
    if (!aegOccurrenceId || !trimmed || submittingNew) return;

    setSubmittingNew(true);

    try {
      const created = await createAegComment(aegOccurrenceId, trimmed);
      if (created?.id) {
        setComments((prev) => sortByCreatedAt([...prev, normalizeComment(created)]));
      } else {
        await loadComments();
      }
      setNewCommentText("");
    } finally {
      setSubmittingNew(false);
    }
  };

  const beginEdit = (comment: AegOccurrenceComment) => {
    if (!comment.id) return;
    setEditingCommentId(comment.id);
    setEditDraft(comment.comment ?? "");
  };

  const cancelEdit = () => {
    setEditingCommentId(null);
    setEditDraft("");
  };

  const saveEdit = async () => {
    if (!aegOccurrenceId || !editingCommentId) return;
    const trimmed = editDraft.trim();
    if (!trimmed) return;

    setSavingEditUuid(editingCommentId);
    try {
      const updated = await updateAegComment(aegOccurrenceId, editingCommentId, trimmed);
      if (updated?.id) {
        setComments((prev) =>
          sortByCreatedAt(prev.map((c) => (c.id === editingCommentId ? normalizeComment(updated) : c))),
        );
      } else {
        await loadComments();
      }
      cancelEdit();
    } finally {
      setSavingEditUuid(null);
    }
  };

  const removeComment = async (commentId: number) => {
    if (!aegOccurrenceId || !commentId) return;
    setDeletingId(commentId);
    try {
      await deleteAegComment(aegOccurrenceId, commentId);
      await loadComments();
    } finally {
      setDeletingId(null);
    }
  };

  return {
    comments,
    loading,
    newCommentText,
    submittingNew,
    editingCommentId,
    editDraft,
    savingEditUuid,
    deletingId,
    showConfirmDialog,
    commentToDelete,

    setNewCommentText,
    setEditDraft,
    submitNewComment,
    beginEdit,
    cancelEdit,
    saveEdit,
    removeComment,
    setShowConfirmDialog,
    setCommentToDelete,
    reloadComments: loadComments,
  };
};

export type AegOccurrenceChatController = ReturnType<typeof useAegOccurrenceChat>;
